// IndexedDB holds the DPoP key pair (private half non-extractable: structured clone keeps the CryptoKey,
// never its bytes) and the session's refresh token, which is useless without that key.
const DB = "emdur-console";
const STORE = "keys";
let generation = 0;
let creating: Promise<CryptoKeyPair> | null = null;

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function run<T>(
  mode: IDBTransactionMode,
  act: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const request = act(transaction.objectStore(STORE));
    transaction.oncomplete = () => {
      db.close();
      resolve(request.result);
    };
    transaction.onabort = transaction.onerror = () => {
      db.close();
      reject(transaction.error ?? request.error);
    };
  });
}

export const get = <T>(name: string) =>
  run<T | undefined>("readonly", (s) => s.get(name));
export const put = (name: string, value: unknown) =>
  run("readwrite", (s) => s.put(value, name));
export const remove = (name: string) => run("readwrite", (s) => s.delete(name));
export function clear() {
  generation++;
  creating = null;
  return run("readwrite", (s) => s.clear());
}
export const entries = () => run("readonly", (s) => s.getAllKeys());

export async function dpopKey(): Promise<CryptoKeyPair> {
  if (creating) return creating;
  const mine = generation;
  const pending = (async () => {
    const existing = await get<CryptoKeyPair>("dpop");
    if (mine !== generation) throw new Error("This sign-in ended.");
    if (existing) return existing;
    const keys = await crypto.subtle.generateKey(
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["sign", "verify"],
    );
    if (mine !== generation) throw new Error("This sign-in ended.");
    await put("dpop", keys);
    if (mine !== generation) throw new Error("This sign-in ended.");
    return keys;
  })();
  creating = pending;
  try {
    return await pending;
  } finally {
    if (creating === pending) creating = null;
  }
}
